import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import './App.css';
import { buildInternationalPhone } from './utils/phone';
import { submitContactRequest, verifyContactRequest } from './api/contactRequests';
import WheelTimePicker from './components/WheelTimePicker';
import GraduateVideoPlayer from './components/GraduateVideoPlayer';
import SiteAnalyticsTracker from './components/SiteAnalyticsTracker';
import { trackSiteEvent } from './utils/siteAnalytics';
import { useSiteMedia } from './hooks/useSiteMedia';
import { useSiteRoute } from './hooks/useSiteRoute';
import { DEFAULT_TEXTS, courseWeeksForPath, isMbaManagementPath, MBA_COURSE_TOOLS, MBA_COURSE_IDENTITY, MBA_FEEDBACK, MBA_FEEDBACK_TITLE } from './content/defaultRouteTexts';
import { MBA_EXTRA_VIDEO_KEYS, MBA_INDUSTRY_CEOS, MBA_INDUSTRY_CEOS_TITLE, MBA_SESSION_PHOTOS, MBA_SESSIONS_TITLE } from './content/localMedia';
import { sectionEnabled, customsAfter } from './content/siteLayout';
import CustomSiteSection from './components/CustomSiteSection';

// Member Home Component - REMOVED

// Course Landing Page Component
const CourseLandingPage = ({ selectedCourse, setCurrentView }) => {
  const { url } = useSiteMedia();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    message: ''
  });

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('תודה על הפנייה! נחזור אליכם בהקדם.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      organization: '',
      message: ''
    });
  };

  if (!selectedCourse) {
    return <div>Loading...</div>;
  }

  return (
    <div className="course-landing-page">
      {/* Header */}
      <div className="course-header">
        <div className="container">
          <div className="header-content">
            <div className="company-logo">
              <img src={url('logo')} alt="Great Leaders Academy Logo" className="logo-image" />
              <h1 className="company-name">Great Leaders Academy</h1>
            </div>
            <button 
              className="back-button"
              onClick={() => setCurrentView('main')}
            >
              חזרה לעמוד הראשי
            </button>
          </div>
        </div>
      </div>

      {/* Course Details */}
      <section className="course-details-section">
        <div className="container">
          <div className="course-details-content">
            <div className="course-hero">
              <h1 className="course-landing-title">{selectedCourse.title}</h1>
              <p className="course-landing-description">{selectedCourse.description}</p>
              
              <div className="course-highlights">
                <div className="highlight-item">
                  <div className="highlight-icon">🎯</div>
                  <div className="highlight-text">
                    <h3>מטרות הלמידה</h3>
                    <p>פיתוח יכולות מנהיגות מתקדמות וכלים מעשיים לשימוש יומיומי</p>
                  </div>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">👥</div>
                  <div className="highlight-text">
                    <h3>קהל היעד</h3>
                    <p>{selectedCourse.details.audience || 'מנהלים, מנהיגים וכל מי שרוצה לפתח יכולות מנהיגות'}</p>
                  </div>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">📚</div>
                  <div className="highlight-text">
                    <h3>תוכן מעשי</h3>
                    <p>דוגמאות מהחיים האמיתיים, תרגילים מעשיים וכלים יישומיים</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="course-info-grid">
              {Object.entries(selectedCourse.details).map(([key, value]) => (
                <div key={key} className="info-item">
                  <span className="info-label">{key === 'duration' ? 'משך' : 
                    key === 'level' ? 'רמה' : 
                    key === 'format' ? 'פורמט' : 
                    key === 'price' ? 'מחיר' : 
                    key === 'audience' ? 'קהל יעד' : key}</span>
                  <span className="info-value">{value}</span>
                </div>
              ))}
            </div>

            <div className="course-benefits">
              <h2 className="benefits-title">מה תקבלו בקורס/ההרצאה?</h2>
              <div className="benefits-grid">
                <div className="benefit-item">
                  <div className="benefit-icon">💡</div>
                  <h4>תובנות מעשיות</h4>
                  <p>כלים וטכניקות מוכחות לפיתוח מנהיגות אפקטיבית</p>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🔄</div>
                  <h4>תרגול מעשי</h4>
                  <p>תרגילים וסימולציות להטמעת החומר הנלמד</p>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">📈</div>
                  <h4>התקדמות מוכחת</h4>
                  <p>שיפור מיידי ביכולות המנהיגות והניהול</p>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🤝</div>
                  <h4>רשת קשרים</h4>
                  <p>הזדמנות ליצור קשרים עם מנהיגים אחרים</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="course-contact-section">
        <div className="container">
          <h2 className="section-title">השאירו פרטים ונחזור אליכם</h2>
          <div className="contact-form-container">
            <form className="course-contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="שם מלא"
                  value={formData.name}
                  onChange={handleFormInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="כתובת אימייל"
                  value={formData.email}
                  onChange={handleFormInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  placeholder="מספר טלפון"
                  value={formData.phone}
                  onChange={handleFormInputChange}
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="organization"
                  placeholder="שם הארגון/המוסד"
                  value={formData.organization}
                  onChange={handleFormInputChange}
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="הודעה (אופציונלי)"
                  value={formData.message}
                  onChange={handleFormInputChange}
                  rows="4"
                ></textarea>
              </div>
              <button type="submit" className="submit-btn">
                שלח פנייה
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>אל תפספסו את ההזדמנות לפתח יכולות מנהיגות מתקדמות עם מומחה בעל ניסיון מוכח! 🌍</p>
          <p className="footer-note">נשמח לשמוע מכם! 🚀</p>
        </div>
      </footer>
    </div>
  );
};

function OptionalImg({ src, alt, className, wrapClassName }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  const img = (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
  return wrapClassName ? <div className={wrapClassName}>{img}</div> : img;
}

function getResponsiveVisibleCount(requested) {
  if (typeof window === 'undefined') return requested;
  const width = window.innerWidth;
  if (width <= 640) return 1;
  if (width <= 960) return Math.min(requested, 2);
  return Math.max(1, requested);
}

function useResponsiveVisibleCount(requested) {
  const [count, setCount] = useState(() => getResponsiveVisibleCount(requested));
  useEffect(() => {
    const apply = () => setCount(getResponsiveVisibleCount(requested));
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [requested]);
  return count;
}

function indexOfNearestChild(track, align) {
  const kids = track.children;
  if (!kids.length) return 0;
  const target = align === 'start'
    ? track.scrollLeft
    : track.scrollLeft + track.clientWidth / 2;
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < kids.length; i += 1) {
    const child = kids[i];
    const point = align === 'start'
      ? child.offsetLeft
      : child.offsetLeft + child.offsetWidth / 2;
    const dist = Math.abs(point - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

function SnapCarousel({
  items,
  renderItem,
  ariaPrev,
  ariaNext,
  className,
  visibleCount: requestedVisibleCount = 1,
  align = 'center',
}) {
  const visibleCount = useResponsiveVisibleCount(requestedVisibleCount);
  const trackRef = useRef(null);
  const jumpTimerRef = useRef(null);
  const programmaticRef = useRef(false);
  const [active, setActive] = useState(0);
  const step = Math.max(1, visibleCount);
  const loop = items.length > 1 && step === 1;

  const maxStart = () => Math.max(0, items.length - (step > 1 ? step : 1));

  const markProgrammatic = (ms = 520) => {
    programmaticRef.current = true;
    window.clearTimeout(jumpTimerRef.current);
    jumpTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false;
    }, ms);
  };

  const scrollToChild = (childIndex, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[childIndex];
    if (!card) return;
    const left = align === 'start'
      ? card.offsetLeft
      : card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
    track.scrollTo({ left, behavior });
  };

  const afterScroll = (fn) => {
    const track = trackRef.current;
    if (!track) {
      fn();
      return;
    }
    window.clearTimeout(jumpTimerRef.current);
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      track.removeEventListener('scrollend', done);
      window.clearTimeout(jumpTimerRef.current);
      programmaticRef.current = false;
      fn();
    };
    track.addEventListener('scrollend', done);
    jumpTimerRef.current = window.setTimeout(done, 480);
  };

  const scrollToStart = (startIndex, behavior = 'smooth') => {
    const track = trackRef.current;
    if (!track || !items.length) return;
    const last = maxStart();
    markProgrammatic();

    if (loop) {
      if (startIndex > last) {
        setActive(0);
        scrollToChild(items.length + 1, behavior);
        afterScroll(() => scrollToChild(1, 'auto'));
        return;
      }
      if (startIndex < 0) {
        setActive(last);
        scrollToChild(0, behavior);
        afterScroll(() => scrollToChild(items.length, 'auto'));
        return;
      }
      const next = Math.min(Math.max(0, startIndex), last);
      scrollToChild(next + 1, behavior);
      setActive(next);
      return;
    }

    let next = startIndex;
    if (next > last) next = 0;
    else if (next < 0) next = last;
    next = Math.min(Math.max(0, next), last);
    scrollToChild(next, behavior);
    setActive(next);
  };

  const pageStartFor = (index) => (
    step > 1 ? Math.min(Math.floor(index / step) * step, maxStart()) : index
  );

  useEffect(() => {
    scrollToStart(0, 'auto');
    return () => window.clearTimeout(jumpTimerRef.current);
  }, [items.length, step]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !items.length) return undefined;
    let settleTimer;

    const syncFromScroll = (finalize) => {
      if (programmaticRef.current) return;
      const displayIndex = indexOfNearestChild(track, align);
      if (loop) {
        const last = Math.max(0, items.length - 1);
        if (displayIndex <= 0) {
          setActive(last);
          if (finalize) {
            programmaticRef.current = true;
            scrollToChild(items.length, 'auto');
            window.setTimeout(() => { programmaticRef.current = false; }, 80);
          }
          return;
        }
        if (displayIndex >= items.length + 1) {
          setActive(0);
          if (finalize) {
            programmaticRef.current = true;
            scrollToChild(1, 'auto');
            window.setTimeout(() => { programmaticRef.current = false; }, 80);
          }
          return;
        }
        setActive(displayIndex - 1);
        return;
      }
      setActive(pageStartFor(displayIndex));
    };

    const onScroll = () => {
      syncFromScroll(false);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => syncFromScroll(true), 90);
    };
    const onScrollEnd = () => {
      window.clearTimeout(settleTimer);
      syncFromScroll(true);
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('scrollend', onScrollEnd);
    return () => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('scrollend', onScrollEnd);
      window.clearTimeout(settleTimer);
    };
  }, [items.length, loop, step, align]);

  const slots = loop
    ? [
        { item: items[items.length - 1], originalIndex: items.length - 1, clone: true },
        ...items.map((item, originalIndex) => ({ item, originalIndex, clone: false })),
        { item: items[0], originalIndex: 0, clone: true },
      ]
    : items.map((item, originalIndex) => ({ item, originalIndex, clone: false }));

  return (
    <div className={className}>
      <div className="ceo-carousel">
        <button
          type="button"
          className="ceo-carousel-nav ceo-carousel-nav--prev"
          onClick={() => scrollToStart(active - step)}
          aria-label={ariaPrev}
        >
          ‹
        </button>
        <div className="ceo-carousel-track" ref={trackRef}>
          {slots.map((slot, displayIndex) => (
            <Fragment key={`snap-${displayIndex}`}>
              {renderItem(
                slot.item,
                slot.originalIndex,
                step > 1
                  ? slot.originalIndex >= active && slot.originalIndex < active + step
                  : slot.originalIndex === active,
                () => scrollToStart(pageStartFor(slot.originalIndex)),
              )}
            </Fragment>
          ))}
        </div>
        <button
          type="button"
          className="ceo-carousel-nav ceo-carousel-nav--next"
          onClick={() => scrollToStart(active + step)}
          aria-label={ariaNext}
        >
          ›
        </button>
      </div>
    </div>
  );
}

function SessionGallery({ photos, title }) {
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    if (openIndex == null) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpenIndex(null);
      if (event.key === 'ArrowLeft') {
        setOpenIndex((current) => (current + 1) % photos.length);
      }
      if (event.key === 'ArrowRight') {
        setOpenIndex((current) => (current - 1 + photos.length) % photos.length);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [openIndex, photos.length]);

  const active = openIndex == null ? null : photos[openIndex];

  return (
    <div className="session-gallery">
      <header className="session-gallery-head">
        <h2 className="session-gallery-title">{title}</h2>
      </header>
      <div className="session-mosaic">
        {photos.map((photo, index) => (
          <button
            type="button"
            className={`session-tile session-tile--${index + 1}`}
            key={photo.src}
            onClick={() => setOpenIndex(index)}
          >
            <img src={photo.src} alt={photo.alt} />
          </button>
        ))}
      </div>
      {active && (
        <div className="session-lightbox" onClick={() => setOpenIndex(null)}>
          <button
            type="button"
            className="session-lightbox-nav session-lightbox-nav--prev"
            aria-label="תמונה קודמת"
            onClick={(event) => {
              event.stopPropagation();
              setOpenIndex((current) => (current - 1 + photos.length) % photos.length);
            }}
          >
            ‹
          </button>
          <img
            src={active.src}
            alt={active.alt}
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            className="session-lightbox-nav session-lightbox-nav--next"
            aria-label="תמונה הבאה"
            onClick={(event) => {
              event.stopPropagation();
              setOpenIndex((current) => (current + 1) % photos.length);
            }}
          >
            ›
          </button>
          <button
            type="button"
            className="session-lightbox-close"
            aria-label="סגירה"
            onClick={() => setOpenIndex(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function IndustryCeoCarousel({ people, title }) {
  return (
    <div className="ceo-carousel-section">
      <h3 className="ceo-carousel-title">{title}</h3>
      <SnapCarousel
        items={people}
        className="ceo-carousel-shell"
        ariaPrev="מנכ״ל קודם"
        ariaNext="מנכ״ל הבא"
        renderItem={(person, index, isActive, onSelect) => (
          <article
            className={`ceo-card${isActive ? ' is-active' : ''}`}
            key={person.name}
            onClick={onSelect}
          >
            <div className="ceo-card-photo">
              <img src={person.src} alt={person.name} />
            </div>
            <div className="ceo-card-copy">
              <strong>{person.name}</strong>
              <span>{person.role}</span>
            </div>
          </article>
        )}
      />
    </div>
  );
}

function App() {
  const { current: siteRoute, path: routePath } = useSiteRoute();
  const videoKeys = useMemo(() => {
    const keys = Array.isArray(siteRoute?.videoKeys) ? [...siteRoute.videoKeys] : [];
    const isMba = isMbaManagementPath(routePath, siteRoute?.path);
    if (!isMba) return keys;
    for (const key of MBA_EXTRA_VIDEO_KEYS) {
      if (!keys.includes(key)) keys.push(key);
    }
    return keys;
  }, [siteRoute?.videoKeys, siteRoute?.path, routePath]);
  const { url, graduateVideos, extraImages } = useSiteMedia(videoKeys, siteRoute?.imageKeys);
  const t = (key) => {
    if (key === 'courseWeeks') {
      return courseWeeksForPath(routePath, siteRoute?.path, typeof window !== 'undefined' ? window.location.pathname : '');
    }
    return (siteRoute?.texts && siteRoute.texts[key]) || DEFAULT_TEXTS[key] || '';
  };
  const showMbaCourseTools = isMbaManagementPath(
    routePath,
    siteRoute?.path,
    typeof window !== 'undefined' ? window.location.pathname : ''
  );
  const partnerCompanies = [
    { key: 'company-elal', src: url('company-elal'), alt: 'אל על' },
    { key: 'company-max', src: url('company-max'), alt: 'MAX' },
    { key: 'company-channel12', src: url('company-channel12'), alt: 'החדשות 12' },
    { key: 'company-extramile', src: url('company-extramile'), alt: 'Extra Mile', variant: 'dark' },
    { key: 'company-refreshglobal', src: url('company-refreshglobal'), alt: 'Refresh Global', variant: 'square' },
    { key: 'company-peker', src: url('company-peker'), alt: 'פקר שפי ברזל לבניין' },
    ...(extraImages || []).map((item) => ({
      key: item.key,
      src: item.src,
      alt: item.alt,
    })),
  ].filter((item) => item.src);

  const showSection = (key) => sectionEnabled(siteRoute?.layout, key);
  const renderCustoms = (afterKey) =>
    customsAfter(siteRoute?.layout, afterKey).map((section) => (
      <CustomSiteSection key={section.key} title={section.title} body={section.body} />
    ));
  const [currentView, setCurrentView] = useState('main'); // 'main' or 'course-landing'
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [successPopup, setSuccessPopup] = useState(null); // { name, purpose }
  const codeInputRefs = useRef([]);
  const videoCarouselRef = useRef(null);
  const videoViewportRef = useRef(null);
  const videoDragRef = useRef({
    active: false,
    startX: 0,
    startScroll: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
  });
  const videoLoopLockRef = useRef(false);
  const [videoTrackIndex, setVideoTrackIndex] = useState(0);
  const [isVideoDragging, setIsVideoDragging] = useState(false);
  const loopedGraduateVideos = [...graduateVideos, ...graduateVideos, ...graduateVideos];

  const pauseCarouselVideos = () => {
    videoCarouselRef.current?.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  };

  const getVideoSlideMetrics = () => {
    const viewport = videoViewportRef.current;
    if (!viewport) return { slideWidth: 0, peek: 0 };
    return {
      slideWidth: viewport.clientWidth * 0.58,
      peek: viewport.clientWidth * 0.21,
    };
  };

  const scrollVideoToIndex = (index, behavior = 'smooth') => {
    const viewport = videoViewportRef.current;
    if (!viewport) return;
    const { slideWidth, peek } = getVideoSlideMetrics();
    viewport.scrollTo({
      left: index * slideWidth - peek,
      behavior,
    });
  };

  const normalizeVideoLoop = () => {
    const viewport = videoViewportRef.current;
    if (!viewport || videoLoopLockRef.current) return;
    const total = graduateVideos.length;
    const { slideWidth, peek } = getVideoSlideMetrics();
    if (!slideWidth) return;
    const index = Math.round((viewport.scrollLeft + peek) / slideWidth);
    if (index < total) {
      videoLoopLockRef.current = true;
      viewport.scrollLeft += total * slideWidth;
      videoLoopLockRef.current = false;
    } else if (index >= total * 2) {
      videoLoopLockRef.current = true;
      viewport.scrollLeft -= total * slideWidth;
      videoLoopLockRef.current = false;
    }
  };

  const goToVideoSlide = (index) => {
    pauseCarouselVideos();
    scrollVideoToIndex(index, 'smooth');
  };

  const handleVideoScroll = () => {
    const viewport = videoViewportRef.current;
    if (!viewport || videoLoopLockRef.current) return;
    const { slideWidth, peek } = getVideoSlideMetrics();
    if (!slideWidth) return;
    const index = Math.round((viewport.scrollLeft + peek) / slideWidth);
    setVideoTrackIndex(index);
    window.clearTimeout(viewport._loopTimer);
    viewport._loopTimer = window.setTimeout(() => {
      if (!videoDragRef.current.active) normalizeVideoLoop();
    }, 140);
  };

  useEffect(() => {
    if (graduateVideos.length) {
      setVideoTrackIndex(graduateVideos.length);
    }
  }, [graduateVideos.length]);

  useEffect(() => {
    const viewport = videoViewportRef.current;
    if (!viewport || !graduateVideos.length) return undefined;
    const { slideWidth, peek } = getVideoSlideMetrics();
    viewport.scrollLeft = graduateVideos.length * slideWidth - peek;

    const onScrollEnd = () => normalizeVideoLoop();
    viewport.addEventListener('scrollend', onScrollEnd);

    const onWheel = (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      viewport.scrollLeft += event.deltaY;
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      viewport.removeEventListener('scrollend', onScrollEnd);
      viewport.removeEventListener('wheel', onWheel);
    };
  }, [graduateVideos.length]);

  const handleVideoPointerDown = (event) => {
    if (event.target.closest('video, button, .story-player')) return;
    if (event.pointerType === 'touch') return;
    const viewport = videoViewportRef.current;
    if (!viewport) return;
    videoDragRef.current = {
      active: true,
      startX: event.clientX,
      startScroll: viewport.scrollLeft,
      lastX: event.clientX,
      lastTime: Date.now(),
      velocity: 0,
    };
    setIsVideoDragging(true);
    pauseCarouselVideos();
    viewport.setPointerCapture(event.pointerId);
  };

  const handleVideoPointerMove = (event) => {
    const drag = videoDragRef.current;
    const viewport = videoViewportRef.current;
    if (!drag.active || !viewport) return;
    const now = Date.now();
    const dt = Math.max(now - drag.lastTime, 1);
    drag.velocity = (event.clientX - drag.lastX) / dt;
    drag.lastX = event.clientX;
    drag.lastTime = now;
    viewport.scrollLeft = drag.startScroll - (event.clientX - drag.startX);
  };

  const handleVideoPointerUp = () => {
    const drag = videoDragRef.current;
    if (!drag.active) return;
    drag.active = false;
    setIsVideoDragging(false);
    const { slideWidth, peek } = getVideoSlideMetrics();
    const viewport = videoViewportRef.current;
    if (!viewport || !slideWidth) return;
    let index = Math.round((viewport.scrollLeft + peek) / slideWidth);
    if (drag.velocity < -0.35) index += 1;
    if (drag.velocity > 0.35) index -= 1;
    scrollVideoToIndex(index, 'smooth');
  };
  const [contactForm, setContactForm] = useState({
    fullName: '',
    contactMethod: 'phone',
    phone: '',
    countryCode: '+972', // Default to Israel
    email: '',
    availableTimes: [{ date: '', fromTime: '', toTime: '' }]
  });

  const countryCodeOptions = [
    { value: '+972', label: '🇮🇱 ישראל (+972)' },
    { value: '+1', label: '🇺🇸 ארה"ב (+1)' },
    { value: '+44', label: '🇬🇧 בריטניה (+44)' },
    { value: '+49', label: '🇩🇪 גרמניה (+49)' },
    { value: '+33', label: '🇫🇷 צרפת (+33)' },
    { value: '+39', label: '🇮🇹 איטליה (+39)' },
    { value: '+34', label: '🇪🇸 ספרד (+34)' },
    { value: '+31', label: '🇳🇱 הולנד (+31)' },
    { value: '+41', label: '🇨🇭 שוויץ (+41)' },
    { value: '+43', label: '🇦🇹 אוסטריה (+43)' },
    { value: '+32', label: '🇧🇪 בלגיה (+32)' },
    { value: '+46', label: '🇸🇪 שוודיה (+46)' },
    { value: '+47', label: '🇳🇴 נורווגיה (+47)' },
    { value: '+45', label: '🇩🇰 דנמרק (+45)' },
    { value: '+358', label: '🇫🇮 פינלנד (+358)' },
    { value: '+7', label: '🇷🇺 רוסיה (+7)' },
    { value: '+86', label: '🇨🇳 סין (+86)' },
    { value: '+81', label: '🇯🇵 יפן (+81)' },
    { value: '+82', label: '🇰🇷 דרום קוריאה (+82)' },
    { value: '+91', label: '🇮🇳 הודו (+91)' },
    { value: '+61', label: '🇦🇺 אוסטרליה (+61)' },
    { value: '+64', label: '🇳🇿 ניו זילנד (+64)' },
    { value: '+27', label: '🇿🇦 דרום אפריקה (+27)' },
    { value: '+971', label: '🇦🇪 איחוד האמירויות (+971)' },
    { value: '+966', label: '🇸🇦 ערב הסעודית (+966)' },
    { value: '+20', label: '🇪🇬 מצרים (+20)' },
    { value: '+90', label: '🇹🇷 טורקיה (+90)' },
    { value: '+351', label: '🇵🇹 פורטוגל (+351)' },
    { value: '+353', label: '🇮🇪 אירלנד (+353)' },
    { value: '+48', label: '🇵🇱 פולין (+48)' },
    { value: '+420', label: '🇨🇿 צ\'כיה (+420)' },
    { value: '+36', label: '🇭🇺 הונגריה (+36)' },
    { value: '+40', label: '🇷🇴 רומניה (+40)' },
    { value: '+30', label: '🇬🇷 יוון (+30)' }
  ];

  useEffect(() => {
    if (showVerificationScreen && codeInputRefs.current[0]) {
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [showVerificationScreen]);

  // Course and lecture data
  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const activeVerificationForm = contactForm;

  const clearVerificationState = () => {
    setShowVerificationScreen(false);
    setVerificationCode('');
    setEnteredCode('');
  };

  const openSuccessPopup = ({ name, purpose }) => {
    clearVerificationState();
    setSuccessPopup({
      name: String(name || '').trim() || 'אורח/ת',
      purpose,
    });
  };

  const closeSuccessPopup = () => {
    setSuccessPopup(null);
  };

  const addTimeSlot = () => {
    setContactForm(prev => ({
      ...prev,
      availableTimes: [...prev.availableTimes, { date: '', fromTime: '', toTime: '' }]
    }));
  };

  const updateTimeSlot = (index, field, value) => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.map((timeSlot, i) => {
        if (i !== index) return timeSlot;
        const next = { ...timeSlot, [field]: value };
        if (field === 'fromTime' && next.toTime && value && next.toTime < value) {
          next.toTime = '';
        }
        return next;
      }),
    }));
  };

  const removeTimeSlot = (index) => {
    setContactForm(prev => ({
      ...prev,
      availableTimes: prev.availableTimes.filter((_, i) => i !== index)
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingContact) return;

    const name = contactForm.fullName.trim();
    if (!name) {
      alert('יש למלא שם מלא');
      return;
    }
    if (contactForm.contactMethod === 'phone' && !contactForm.phone.trim()) {
      alert('יש למלא מספר טלפון');
      return;
    }
    if (contactForm.contactMethod === 'email' && !contactForm.email.trim()) {
      alert('יש למלא כתובת מייל');
      return;
    }
    const hasTime = (contactForm.availableTimes || []).some(
      (slot) => slot.date && slot.fromTime && slot.toTime
    );
    if (!hasTime) {
      alert('יש לבחור לפחות זמן פנוי אחד');
      return;
    }

    setIsSubmittingContact(true);
    try {
      const phone =
        contactForm.contactMethod === 'phone'
          ? buildInternationalPhone(contactForm.countryCode, contactForm.phone)
          : undefined;
      const email =
        contactForm.contactMethod === 'email'
          ? contactForm.email.trim()
          : undefined;

      const verifyResult = await verifyContactRequest({
        contactMethod: contactForm.contactMethod,
        preferredContactMethod: contactForm.contactMethod,
        phone,
        email,
      });

      if (verifyResult.success) {
        setVerificationCode(verifyResult.data.verificationCode);
        setShowVerificationScreen(true);
        trackSiteEvent('contact_start', { path: routePath, title: siteRoute?.title });
      } else {
        throw new Error(verifyResult.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (err) {
      alert(`❌ ${err.message || 'אופס! משהו השתבש בשליחת קוד האימות. אנא נסה שנית.'}`);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      if (enteredCode.trim() !== verificationCode) {
        alert('❌ קוד האימות שגוי. אנא נסה שנית.');
        setEnteredCode('');
        return;
      }

      await submitContactRequest({
        fullName: contactForm.fullName,
        contactMethod: contactForm.contactMethod,
        phone: contactForm.phone,
        countryCode: contactForm.countryCode,
        email: contactForm.email,
        availableTimes: contactForm.availableTimes,
        verificationCode: enteredCode.trim(),
      });
      trackSiteEvent('contact_submit', { path: routePath, title: siteRoute?.title });

      const name = contactForm.fullName.trim();
      setContactForm({
        fullName: '',
        contactMethod: 'phone',
        phone: '',
        countryCode: '+972',
        email: '',
        availableTimes: [{ date: '', fromTime: '', toTime: '' }],
      });
      openSuccessPopup({ name, purpose: 'talk' });
    } catch (err) {
      alert(`❌ ${err.message || 'שליחת הטופס נכשלה. נסו שוב.'}`);
    } finally {
      setIsVerifying(false);
    }
  };

  // Removed: posts state variable
  // Removed: newPost, showArticle, currentArticle, currentLesson state variables

  // Removed: egoArticle, dailyRoutineArticle, holisticLeadershipArticle
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organizationType: '',
    organizationName: '',
    serviceType: '',
    specificService: '',
    message: ''
  });

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // כאן תוכל להוסיף לוגיקה לשליחת הטופס
    alert('תודה על הפנייה! נחזור אליך בהקדם.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          organizationType: '',
          organizationName: '',
          serviceType: '',
          specificService: '',
          message: ''
        });
  };

  // Removed: handleSubscribe, handlePaymentComplete, handlePayPalPayment

  // Removed: toggleComments, handlePostSubmit functions


  // Scroll Animation Effect — re-bind when async blocks (videos) mount
  useEffect(() => {
    const inIframe = window.self !== window.top;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, {
      threshold: 0,
      rootMargin: '0px'
    });

    const animatedElements = document.querySelectorAll('.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale');
    animatedElements.forEach((el) => {
      if (inIframe) el.classList.add('animate-in');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [siteRoute, graduateVideos.length]);

  // Checkout Screen Component - REMOVED
  /*const CheckoutScreen = () => (
    <div className="checkout-screen">
      <div className="container">
        <button 
          className="back-button"
          onClick={() => setCurrentView('pricing')}
        >
          חזרה
          <span className="back-icon">←</span>
        </button>
        
        <div className="checkout-content">
          <div className="checkout-card">
            <div className="order-summary">
              <h2>סיכום הזמנה</h2>
              <div className="order-item">
                <span className="item-name">מנוי חודשי לקהילת המנהיגים</span>
                <span className="item-price">99 ₪</span>
              </div>
              <div className="order-total">
                <span className="total-label">סה"כ לתשלום:</span>
                <span className="total-price">99 ₪</span>
              </div>
            </div>
            
            <div className="payment-form">
              <h3>פרטי תשלום</h3>
              <div className="form-group">
                <input type="text" placeholder="שם מלא" />
              </div>
              <div className="form-group">
                <input type="email" placeholder="כתובת אימייל" />
              </div>
              <div className="form-group">
                <input type="password" placeholder="סיסמה" />
              </div>
              <div className="form-group">
                <input type="tel" placeholder="מספר טלפון" />
              </div>
              <div className="form-group">
                <input type="text" placeholder="מספר כרטיס אשראי" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input type="text" placeholder="תאריך תפוגה (MM/YY)" />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="CVV" />
                </div>
              </div>
              
              <button className="pay-button" onClick={handlePayPalPayment}>
                <span className="pay-text">שלם עם PayPal</span>
                <span className="pay-amount">99 ₪</span>
              </button>
              
              <div className="security-badges">
                <span className="security-text">🔒 תשלום מאובטח</span>
                <span className="security-text">🛡️ מוגן SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );*/

  // Pricing Screen Component - REMOVED
  /*const PricingScreen = () => (
    <div className="pricing-screen">
      
      <div className="container">
        <button 
          className="back-button"
          onClick={() => setCurrentView('main')}
        >
          חזרה
          <span className="back-icon">←</span>
        </button>
        
        <div className="pricing-hero">
          <h1 className="pricing-title">
            <span className="title-main">הצטרפו לקהילת המנהיגים</span>
          </h1>
          <p className="pricing-subtitle">
            התחל את המסע שלך לפיתוח מנהיגות עם כלים מעשיים ותמיכה מלאה
          </p>
        </div>
        
        <div className="benefits-grid">
          <div className="benefit-card benefit-card-1">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">📚</span>
              </div>
              <div className="benefit-number">01</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">גישה לכל הקורסים הדיגיטליים</h3>
              <p className="benefit-description">
                קורסים מקיפים בפיתוח מנהיגות עם ד"ר משה קטן, כולל שיטת C.O.D הייחודית
              </p>
              <div className="benefit-features">
                <span className="feature-tag">+15 קורסים</span>
                <span className="feature-tag">תמיכה מלאה</span>
              </div>
            </div>
          </div>
          
          <div className="benefit-card benefit-card-2">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">👥</span>
              </div>
              <div className="benefit-number">02</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">חברות בקהילת המנהיגים</h3>
              <p className="benefit-description">
                שיתוף דילמות והתפתחות עם מנהיגים אחרים, קבלת ייעוץ ותמיכה מהקהילה
              </p>
              <div className="benefit-features">
                <span className="feature-tag">קהילה פעילה</span>
                <span className="feature-tag">ייעוץ אישי</span>
              </div>
            </div>
          </div>
          
          <div className="benefit-card benefit-card-3">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">🎥</span>
              </div>
              <div className="benefit-number">03</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">תוכן בלעדי</h3>
              <p className="benefit-description">
                וידאו, הרצאות ותכנים ייחודיים רק למנויים, כולל חומרים שלא זמינים במקום אחר
              </p>
              <div className="benefit-features">
                <span className="feature-tag">תוכן בלעדי</span>
                <span className="feature-tag">עדכונים שוטפים</span>
              </div>
            </div>
          </div>
          
          <div className="benefit-card benefit-card-4">
            <div className="benefit-header">
              <div className="benefit-icon-wrapper">
                <span className="benefit-icon">🎤</span>
              </div>
              <div className="benefit-number">04</div>
            </div>
            <div className="benefit-content">
              <h3 className="benefit-title">הרצאה חודשית לקהילה</h3>
              <p className="benefit-description">
                הרצאה חיה עם ד"ר משה קטן פעם בחודש, כולל שאלות ותשובות אישיות
              </p>
              <div className="benefit-features">
                <span className="feature-tag">הרצאה חיה</span>
                <span className="feature-tag">Q&A אישי</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pricing-section">
          <div className="pricing-card">
            <div className="pricing-header">
              <div className="pricing-badge">
                <span className="badge-text">מחיר מיוחד</span>
                <span className="badge-discount">-50%</span>
              </div>
              <h2 className="pricing-card-title">מנוי חודשי</h2>
            </div>
            
            <div className="price-display">
              <div className="price-amount">
                <span className="currency">₪</span>
                <span className="amount">99</span>
                <span className="period">לחודש</span>
              </div>
              <div className="price-details">
                <span className="original-price">₪198 לחודש</span>
                <span className="savings">חיסכון של ₪1,188 בשנה!</span>
              </div>
            </div>
            
            <div className="pricing-features">
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>גישה לכל הקורסים ללא הגבלה</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>חברות בקהילת המנהיגים</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>תוכן בלעדי ועדכונים שוטפים</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>הרצאה חודשית חיה</span>
              </div>
              <div className="feature-item">
                <span className="check-icon">✓</span>
                <span>תמיכה טכנית 24/7</span>
              </div>
            </div>
            
            <button className="subscribe-button" onClick={handleSubscribe}>
              <span className="button-text">התחל עכשיו</span>
              <span className="button-price">99 ₪ לחודש</span>
              <div className="button-glow"></div>
            </button>
            
            <div className="guarantee">
              <span className="guarantee-icon">🛡️</span>
              <span className="guarantee-text">ערבות החזר כספי מלא למשך 30 יום</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );*/

  return (
    <div className="App" dir="rtl">
      <SiteAnalyticsTracker path={routePath} title={siteRoute?.title} />
      {currentView === 'course-landing' ? (
        <CourseLandingPage 
          selectedCourse={selectedCourse}
          setCurrentView={setCurrentView}
        />
      ) : (
        <>
          {showSection('header') && (
          <div className="company-header">
            <div className="container">
              <div className="header-content">
                <div className="company-logo">
                  <OptionalImg src={url('logo')} alt="Great Leaders Academy Logo" className="logo-image" />
                  <h1 className="company-name">Great Leaders Academy</h1>
                </div>
              </div>
            </div>
          </div>
          )}
          {renderCustoms('header')}


      {showSection('philosophy') && (
      <section className="philosophy-section scroll-animate">
        <div className="container">
          <div className="philosophy-content">
            <h2 className="philosophy-title">{t('philosophyTitle')}</h2>
            <p className="philosophy-text">
              {t('philosophyText')}
            </p>
            <div className="mission-section">
              <h3 className="mission-title">{t('missionTitle')}</h3>
              <p className="mission-text">
                {t('missionText')}
              </p>
            </div>
            <div className="vision-section">
              <h3 className="vision-title">{t('visionTitle')}</h3>
              <p className="vision-text">
                {t('visionText')}
              </p>
            </div>
            <div className="values-section">
              <h3 className="values-title">הערכים שלנו:</h3>
              <div className="values-grid">
                <div className="value-item">
                  <span className="value-icon">😊</span>
                  <h4>שמחה</h4>
                  <p>יצירת סביבה חיובית ומעוררת השראה</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🤝</span>
                  <h4>כבוד</h4>
                  <p>הערכה והתחשבות בכל אדם ובכל מצב</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">💡</span>
                  <h4>חשיבה חיובית</h4>
                  <p>גישה אופטימית ופתרון בעיות יצירתי</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🚀</span>
                  <h4>התפתחות מתמדת</h4>
                  <p>התפתחות והשתפרות מתמדת במעגלי החיים</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}
      {renderCustoms('philosophy')}

      {showSection('offer') && (
      <section className="benefits-section scroll-animate">
        <div className="container">
          <h2 className="section-title">{t('offerTitle')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">🌱</div>
              <h3>{t('offer1Title')}</h3>
              <p>{t('offer1Text')}</p>
            </div>
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">👥</div>
              <h3>{t('offer2Title')}</h3>
              <p>{t('offer2Text')}</p>
            </div>
            <div className="benefit-card scroll-animate-scale">
              <div className="benefit-icon">🧭</div>
              <h3>{t('offer3Title')}</h3>
              <p>{t('offer3Text')}</p>
            </div>
          </div>
        </div>
      </section>
      )}
      {renderCustoms('offer')}

      {showSection('course') && (
      <section className="course-about-section scroll-animate">
        <div className="container">
          <h2 className="section-title">{t('courseTitle')}</h2>

          {showMbaCourseTools && (
            <article className="course-ticket" aria-label="פרטי הקורס">
              <header className="course-ticket-head">
                <div className="course-ticket-top">
                  <span>{MBA_COURSE_IDENTITY.semester}</span>
                  <span className="course-ticket-sep" aria-hidden="true" />
                  <span>{MBA_COURSE_IDENTITY.year}</span>
                </div>
                <span className="course-ticket-number">{MBA_COURSE_IDENTITY.number}</span>
                <h3>{MBA_COURSE_IDENTITY.name}</h3>
                <p className="course-ticket-note">{MBA_COURSE_IDENTITY.note}</p>
              </header>
              <div className="course-ticket-body">
                <div className="course-ticket-col">
                  <span className="course-ticket-kicker">{MBA_COURSE_IDENTITY.whenLabel}</span>
                  <p className="course-ticket-primary">
                    {MBA_COURSE_IDENTITY.day}
                    <span className="course-ticket-ltr">{MBA_COURSE_IDENTITY.hours}</span>
                  </p>
                  <p className="course-ticket-secondary course-ticket-ltr">
                    {MBA_COURSE_IDENTITY.dates}
                  </p>
                </div>
                <div className="course-ticket-col">
                  <span className="course-ticket-kicker">{MBA_COURSE_IDENTITY.whereLabel}</span>
                  <p className="course-ticket-primary">
                    {MBA_COURSE_IDENTITY.building}
                    <span>{MBA_COURSE_IDENTITY.room}</span>
                  </p>
                </div>
              </div>
            </article>
          )}

          <div className="course-about-stats">
            <span>{t('courseWeeks')}</span>
            <span>אישי וקולקטיבי</span>
            <span>תיאוריה ופרקטיקה</span>
          </div>

          <blockquote className="course-about-quote">
            <p>{t('courseQuote')}</p>
            <cite>{t('courseQuoteCite')}</cite>
          </blockquote>

          <p className="course-about-lead">
            {t('courseLead')}
          </p>

          {showMbaCourseTools && (
            <div className="course-tools">
              <div className="course-tools-purpose">
                <p className="course-tools-purpose-not">{MBA_COURSE_TOOLS.purposeNot}</p>
                <p className="course-tools-purpose-yes">{MBA_COURSE_TOOLS.purposeYes}</p>
              </div>
              <p className="course-tools-lead">{MBA_COURSE_TOOLS.lead}</p>
              <ol className="course-tools-list">
                {MBA_COURSE_TOOLS.items.map((item, index) => (
                  <li
                    key={item}
                    className={`course-tools-item${index === MBA_COURSE_TOOLS.items.length - 1 ? ' course-tools-item--highlight' : ''}`}
                  >
                    <span className="course-tools-index" aria-hidden="true">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <p>{item}</p>
                  </li>
                ))}
              </ol>
              <p className="course-tools-punch">{MBA_COURSE_TOOLS.punch}</p>
            </div>
          )}

          <div className="course-about-pillars">
            <div className="course-about-pillar">
              <span className="course-about-pillar-num">01</span>
              <span className="course-about-pillar-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </span>
              <h3>הבנה תיאורטית</h3>
              <p>תיאוריות מנהיגות משמעותיות ויישומן בעולם דינמי של VUCA</p>
            </div>
            <div className="course-about-pillar">
              <span className="course-about-pillar-num">02</span>
              <span className="course-about-pillar-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
              </span>
              <h3>התנסות חווייתית</h3>
              <p>מיני־משימות לפיתוח אישי, התבוננות עצמית ומינוף עוצמות</p>
            </div>
            <div className="course-about-pillar">
              <span className="course-about-pillar-num">03</span>
              <span className="course-about-pillar-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                </svg>
              </span>
              <h3>פרויקט בשטח</h3>
              <p>חקירת תהליך פיתוח מנהיגות בארגון אמיתי, והצעת דרכים פרקטיות</p>
            </div>
          </div>

          <h3 className="course-about-subtitle">בתום הקורס תבינו</h3>
          <div className="course-about-outcomes">
            <div className="course-about-outcome">ההבדל בין מנהיגות לפיתוח מנהיגות</div>
            <div className="course-about-outcome">פיתוח מנהיג כחלק מפיתוח מנהיגות</div>
            <div className="course-about-outcome">פיתוח קולקטיבי בארגון</div>
            <div className="course-about-outcome">המשקל של מנהיגות בניהול</div>
            <div className="course-about-outcome">תהליכים פורמליים ובלתי פורמליים</div>
            <div className="course-about-outcome">מינוף עוצמות אישיות בתהליך הפיתוח</div>
          </div>

          <h3 className="course-about-subtitle">מסלול הקורס</h3>
          <ol className="course-about-path">
            <li>
              <span className="course-about-path-index">01</span>
              <div className="course-about-path-body">
                <strong>תיאוריות מרכזיות במנהיגות</strong>
                <p>חיבור תיאוריות משמעותיות לתפקיד הניהולי, והשפעת VUCA על הארגון ועל המנהיגות</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">02</span>
              <div className="course-about-path-body">
                <strong>למה פיתוח מנהיגות</strong>
                <p>ההבדל בין מנהיגות לפיתוח מנהיגות, והמחוללים המרכזיים בסביבה העסקית</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">03</span>
              <div className="course-about-path-body">
                <strong>פיתוח המנהיג</strong>
                <p>תפקיד היחיד בתהליך, ומשקל המנהיגות בתפקידו של המנהל</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">04</span>
              <div className="course-about-path-body">
                <strong>פיתוח הקולקטיב</strong>
                <p>תהליכים פורמליים ובלתי פורמליים, ופרספקטיבה חדשה על פיתוח מנהיגות</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">05</span>
              <div className="course-about-path-body">
                <strong>מנהלים בשטח</strong>
                <p>ניתוח פעולות בשטח ומודלים תיאורטיים לפיתוח מנהיגות בלתי פורמלי</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">06</span>
              <div className="course-about-path-body">
                <strong>פרקטיקות והתערבויות</strong>
                <p>כלים לקידום פיתוח מנהיגות אישי וקולקטיבי, מהספרות ומהעולם העסקי</p>
              </div>
            </li>
            <li>
              <span className="course-about-path-index">07</span>
              <div className="course-about-path-body">
                <strong>פרויקטים וסיכום</strong>
                <p>שיתוף ממצאים, דרכי פעולה להגדלת היכולת בארגון, ומשוב עמיתים</p>
              </div>
            </li>
          </ol>
        </div>
      </section>
      )}
      {renderCustoms('course')}

      {showSection('experience') && (
      <section className="experience-section scroll-animate">
        <div className="container">
          <h2 className="section-title">{t('experienceTitle')}</h2>
          <div className="experience-content">
            <p className="experience-description">
              {t('experienceText')}
            </p>
            
            <div className="tau-logos">
              <div className="tau-logos-grid">
                <OptionalImg wrapClassName="tau-logo-item" src={url('tau-logo')} alt="אוניברסיטת תל אביב" className="tau-logo" />
                <OptionalImg wrapClassName="tau-logo-item" src={url('coller-logo')} alt="פקולטה לניהול - אוניברסיטת תל אביב" className="tau-logo" />
              </div>
            </div>

            <div className="partner-companies-section">
              <h3 className="partner-companies-title">חברות ששיתפנו איתן פעולה בקורסים</h3>
              <SnapCarousel
                items={partnerCompanies}
                className="partner-carousel-shell"
                visibleCount={3}
                align="start"
                ariaPrev="חברה קודמת"
                ariaNext="חברה הבאה"
                renderItem={(company, index, isActive, onSelect) => (
                  <button
                    type="button"
                    className={`partner-company-item${company.variant === 'dark' ? ' partner-company-item--dark' : ''}${company.variant === 'square' ? ' partner-company-item--square' : ''}${isActive ? ' is-active' : ''}`}
                    key={company.key}
                    onClick={onSelect}
                    aria-label={company.alt}
                  >
                    <img
                      src={company.src}
                      alt={company.alt}
                      className="partner-company-logo"
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  </button>
                )}
              />
            </div>

            {showMbaCourseTools && (
              <IndustryCeoCarousel
                people={MBA_INDUSTRY_CEOS.map((person) => ({
                  ...person,
                  src: url(person.imageKey),
                })).filter((person) => person.src)}
                title={MBA_INDUSTRY_CEOS_TITLE}
              />
            )}
          </div>
        </div>
      </section>
      )}
      {renderCustoms('experience')}

      {showSection('videos') && (
      <section id="videos" className="experience-section scroll-animate">
        <div className="container">
            <div className="video-testimonials-section">
              <h3 className="video-section-title">{t('videosTitle')}</h3>
              {graduateVideos.length === 0 ? (
                <p className="video-empty-hint">הסרטונים ייטענו כאן ברגע שהמדיה תהיה זמינה</p>
              ) : (
              <div className="video-carousel" ref={videoCarouselRef}>
                <button
                  type="button"
                  className="video-carousel-nav video-carousel-nav--prev"
                  onClick={() => goToVideoSlide(videoTrackIndex - 1)}
                  aria-label="המלצה קודמת"
                >
                  ‹
                </button>
                <div
                  ref={videoViewportRef}
                  className={`video-carousel-viewport${isVideoDragging ? ' is-dragging' : ''}`}
                  onScroll={handleVideoScroll}
                  onPointerDown={handleVideoPointerDown}
                  onPointerMove={handleVideoPointerMove}
                  onPointerUp={handleVideoPointerUp}
                  onPointerCancel={handleVideoPointerUp}
                >
                  <div className="video-carousel-track">
                    {loopedGraduateVideos.map((graduate, index) => {
                      const total = graduateVideos.length;
                      const realIndex = index % total;
                      const activeIndex = ((videoTrackIndex % total) + total) % total;
                      const isPeek = realIndex !== activeIndex;
                      return (
                        <div
                          className={`video-carousel-slide${isPeek ? ' is-peek' : ''}`}
                          key={`${graduate.name}-${index}`}
                          dir="rtl"
                        >
                          <div className="video-testimonial-card">
                            <GraduateVideoPlayer src={graduate.src} name={graduate.name} role={graduate.role} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  className="video-carousel-nav video-carousel-nav--next"
                  onClick={() => goToVideoSlide(videoTrackIndex + 1)}
                  aria-label="המלצה הבאה"
                >
                  ›
                </button>
              </div>
              )}
            </div>
        </div>
      </section>
      )}
      {renderCustoms('videos')}

      {showMbaCourseTools && (
      <section className="experience-section scroll-animate session-section">
        <div className="container">
          <SessionGallery
            photos={MBA_SESSION_PHOTOS.map((photo) => ({
              src: url(photo.imageKey),
              alt: photo.alt,
            })).filter((photo) => photo.src)}
            title={MBA_SESSIONS_TITLE}
          />
        </div>
      </section>
      )}

      {showSection('feedback') && (
      <section className="experience-section scroll-animate">
        <div className="container">
            {showMbaCourseTools ? (
              <div className="reviews-showcase">
                <header className="reviews-head">
                  <h2 className="section-title">{MBA_FEEDBACK_TITLE}</h2>
                </header>
                <SnapCarousel
                  items={MBA_FEEDBACK}
                  className="feedback-carousel-shell"
                  visibleCount={1}
                  align="center"
                  ariaPrev="ביקורת קודמת"
                  ariaNext="ביקורת הבאה"
                  renderItem={(item, index, isActive) => (
                    <article
                      className={`review-card${isActive ? ' is-active' : ''}`}
                      key={item.key}
                    >
                      <span className="review-card-mark" aria-hidden="true">״</span>
                      <div className="review-card-text">
                        {(item.paragraphs || [item.text]).filter(Boolean).map((paragraph, paragraphIndex) => (
                          <p key={`${item.key}-p-${paragraphIndex}`}>{paragraph}</p>
                        ))}
                      </div>
                    </article>
                  )}
                />
              </div>
            ) : (
            <div className="feedback-grid">
              <div className="feedback-card scroll-animate-left">
                <p className="feedback-text">
                  {t('feedback1')}
                </p>
              </div>
              <div className="feedback-card scroll-animate">
                <p className="feedback-text">
                  {t('feedback2')}
                </p>
              </div>
              <div className="feedback-card scroll-animate-right">
                <p className="feedback-text">
                  {t('feedback3')}
                </p>
              </div>
            </div>
            )}
        </div>
      </section>
      )}
      {renderCustoms('feedback')}

      {showSection('instructor') && (
      <section className="instructor-section scroll-animate">
        <div className="container">
          <div className="instructor-content">
            <div className="instructor-image">
              <div className="instructor-portrait">
                <OptionalImg src={url('dr-moshe-katan')} alt="ד״ר משה קטן" className="dr-moshe-photo" />
              </div>
            </div>
            <div className="instructor-info">
              <h3>{t('instructorName')}</h3>
              <div className="instructor-content-wrapper">
                <div className="certificate-section">
                  <div className="certificate-container">
                    <OptionalImg src={url('phd-certificate')} alt="תעודת דוקטורט מאוניברסיטת פוזנן" className="certificate-image" />
                  </div>
                </div>
                <div className="instructor-text-content">
                  <p className="instructor-description">
                    {t('instructorBio1')}
                  </p>
                  <p className="instructor-description">
                    {t('instructorBio2')}
                  </p>
                </div>
              </div>
              <div className="instructor-achievements">
                <div className="achievement">
                  <span className="achievement-icon">🏆</span>
                  <span>מומחה לפיתוח מנהיגות עסקית מעשית</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">⭐</span>
                  <span>גילוי שיטת C.O.D במחקר דוקטורט</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">🎯</span>
                  <span>30 שנות ניסיון בחברות בינלאומיות</span>
                </div>
                <div className="achievement">
                  <span className="achievement-icon">💼</span>
                  <span>Chair ב-Vistage</span>
                </div>
              </div>
              
              <div className="instructor-logos">
                <div className="logos-grid">
                  <OptionalImg wrapClassName="logo-item" src={url('poznan-university')} alt="University of Poznan" className="logo-image" />
                  <OptionalImg wrapClassName="logo-item" src={url('coca-cola')} alt="Coca-Cola" className="logo-image" />
                  <OptionalImg wrapClassName="logo-item" src={url('carlsberg')} alt="Carlsberg" className="logo-image" />
                  <OptionalImg wrapClassName="logo-item" src={url('strauss')} alt="Strauss Elite" className="logo-image" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}
      {renderCustoms('instructor')}

      {showSection('contact') && (
      <section className="app-download-section scroll-animate">
        <div className="container">
          <div className="contact-section-heading">
            <h2 className="section-title">{t('contactTitle')}</h2>
          </div>

          <div className="contact-inline-form">
            <form onSubmit={handleContactSubmit}>
              <div className="form-group form-group--name">
                <label htmlFor="contact-fullName">שם מלא</label>
                <div className="input-shell">
                  <span className="input-shell-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <input
                    id="contact-fullName"
                    type="text"
                    value={contactForm.fullName}
                    onChange={(e) => handleContactFormChange('fullName', e.target.value)}
                    placeholder="איך קוראים לכם?"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group form-group--method">
                <label>אמצעי יצירת קשר מועדף</label>
                <div className="radio-group">
                  <label className={`contact-method-option${contactForm.contactMethod === 'phone' ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="contactMethod"
                      value="phone"
                      checked={contactForm.contactMethod === 'phone'}
                      onChange={(e) => handleContactFormChange('contactMethod', e.target.value)}
                    />
                    <span className="method-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                      </svg>
                    </span>
                    <span className="method-text">
                      <span className="method-title">טלפון</span>
                      <span className="method-hint">שיחה או הודעה</span>
                    </span>
                  </label>
                  <label className={`contact-method-option${contactForm.contactMethod === 'email' ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="contactMethod"
                      value="email"
                      checked={contactForm.contactMethod === 'email'}
                      onChange={(e) => handleContactFormChange('contactMethod', e.target.value)}
                    />
                    <span className="method-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    <span className="method-text">
                      <span className="method-title">מייל</span>
                      <span className="method-hint">תכתובת נוחה</span>
                    </span>
                  </label>
              </div>
              </div>
              
              {contactForm.contactMethod === 'phone' ? (
                <div className="form-group form-group--contact" key="phone-field">
                  <label htmlFor="contact-phone">טלפון</label>
                  <div className="phone-input-container">
                    <div className="input-shell phone-number-shell">
                      <input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => handleContactFormChange('phone', e.target.value)}
                        placeholder="מספר טלפון"
                        required
                      />
                    </div>
                    <select
                      value={contactForm.countryCode}
                      onChange={(e) => handleContactFormChange('countryCode', e.target.value)}
                      className="country-code-select"
                      aria-label="קידומת מדינה"
                    >
                      {countryCodeOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
              </div>
              ) : (
                <div className="form-group form-group--contact" key="email-field">
                  <label htmlFor="contact-email">מייל</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    <input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => handleContactFormChange('email', e.target.value)}
                      placeholder="name@example.com"
                      required
                    />
                  </div>
            </div>
              )}

              <div className="form-group form-group--times">
                <label className="times-main-label">זמנים פנויים</label>
                {contactForm.availableTimes.map((timeSlot, index) => (
                  <div key={index} className="time-slot-container" style={{ '--slot-i': index }}>
                    <div className="time-slot-header">
                      <h4 className="time-slot-title">
                        <span className="time-slot-badge">{index + 1}</span>
                        זמן פנוי
                      </h4>
                      {contactForm.availableTimes.length > 1 && (
                        <button
                          type="button"
                          className="remove-time"
                          onClick={() => removeTimeSlot(index)}
                          title="הסר זמן"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    
                    <div className="date-picker-section">
                      <label className="time-label">תאריך</label>
                      <input
                        type="date"
                        value={timeSlot.date}
                        onChange={(e) => updateTimeSlot(index, 'date', e.target.value)}
                        className="date-input"
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    <div className="time-pickers-section">
                      <div className="time-picker-group">
                        <WheelTimePicker
                          value={timeSlot.fromTime}
                          onChange={(value) => updateTimeSlot(index, 'fromTime', value)}
                          label="משעה"
                          required
                        />
                      </div>
                      <div className="time-picker-group">
                        <WheelTimePicker
                          value={timeSlot.toTime}
                          onChange={(value) => updateTimeSlot(index, 'toTime', value)}
                          label="עד שעה"
                          disabled={!timeSlot.fromTime}
                          minTime={timeSlot.fromTime}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="add-time-button"
                  onClick={addTimeSlot}
                >
                  <span className="add-icon">+</span>
                  הוסף זמן נוסף
                </button>
              </div>

              <button 
                type="submit" 
                className="submit-contact"
                disabled={isSubmittingContact}
              >
                <span className="submit-contact-label">
                  {isSubmittingContact ? 'שולח...' : 'שלחו אלינו'}
                </span>
                {!isSubmittingContact && (
                  <span className="submit-contact-arrow" aria-hidden="true">←</span>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
      )}
      {renderCustoms('contact')}

          {showSection('footer') && (
          <footer className="footer scroll-animate">
            <div className="container">
              <p>אל תפספסו את ההזדמנות לפתח יכולות מנהיגות מתקדמות עם מומחה בעל ניסיון מוכח! 🌍</p>
              <p className="footer-note">נשמח לשמוע מכם! 🚀</p>
            </div>
          </footer>
          )}
          {renderCustoms('footer')}
        </>
      )}

      {/* Verification Screen */}
      {showVerificationScreen && (
        <div className="contact-popup-overlay verification-overlay" onClick={() => {
          clearVerificationState();
        }}>
          <div className="contact-popup verification-popup" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup" onClick={() => {
              clearVerificationState();
            }}>✕</button>
            
            <div className="verification-header">
              <div className="verification-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none"/>
                  <path d="M8 12l2 2 4-4" stroke="#ffc915" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 className="verification-title">אימות קשר</h2>
              <p className="verification-subtitle">
                {activeVerificationForm.contactMethod === 'email'
                  ? `שלחנו קוד אימות למייל ${activeVerificationForm.email}`
                  : `שלחנו קוד אימות למספר ${buildInternationalPhone(activeVerificationForm.countryCode, activeVerificationForm.phone)}`}
              </p>
            </div>

            <form onSubmit={handleVerificationSubmit} className="verification-form">
              <div className="verification-code-container">
                <label className="verification-label">קוד האימות</label>
                <div className="code-input-wrapper">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      ref={(el) => (codeInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      className={`code-digit${enteredCode[index] ? ' has-value' : ''}`}
                      value={enteredCode[index] || ''}
                      aria-label={`ספרה ${index + 1} מתוך 4`}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                        if (value) {
                          const newCode = enteredCode.split('');
                          newCode[index] = value;
                          const updatedCode = newCode.join('').slice(0, 4);
                          setEnteredCode(updatedCode);
                          // Auto-focus next input
                          if (index < 3 && codeInputRefs.current[index + 1]) {
                            codeInputRefs.current[index + 1].focus();
                          }
                        } else {
                          // Handle deletion
                          const newCode = enteredCode.split('');
                          newCode[index] = '';
                          setEnteredCode(newCode.join(''));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !enteredCode[index] && index > 0) {
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowLeft' && index > 0) {
                          e.preventDefault();
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowRight' && index < 3) {
                          e.preventDefault();
                          codeInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
                        if (pastedData) {
                          setEnteredCode(pastedData);
                          const nextIndex = Math.min(index + pastedData.length, 3);
                          codeInputRefs.current[nextIndex]?.focus();
                        }
                      }}
                      maxLength="1"
                      required
                    />
                  ))}
                </div>
                <p className="code-hint">הכנס את הקוד בן 4 הספרות שקיבלת</p>
              </div>

              <button 
                type="submit" 
                className="verify-submit-btn"
                disabled={isVerifying || enteredCode.length !== 4}
              >
                {isVerifying ? (
                  <>
                    <span className="spinner"></span>
                    מאמת...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    אמת ושלח
                  </>
                )}
              </button>

              <button
                type="button"
                className="back-to-form-btn"
                onClick={() => {
                  clearVerificationState();
                }}
              >
                ← חזור לטופס
              </button>
            </form>
          </div>
        </div>
      )}

      {successPopup && (
        <div
          className="contact-popup-overlay success-popup-overlay"
          onClick={closeSuccessPopup}
        >
          <div
            className="contact-popup success-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="success-popup-title"
          >
            <button
              type="button"
              className="close-popup"
              onClick={closeSuccessPopup}
              aria-label="סגירה"
            >
              ✕
            </button>

            <div className="success-popup-icon" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none"/>
                <path d="M8 12l2 2 4-4" stroke="#383c7f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>

            <h2 id="success-popup-title" className="success-popup-title">
              היי {successPopup.name}!
            </h2>
            <p className="success-popup-message">
              הפרטים התקבלו בהצלחה. ניצור איתך קשר בהקדם האפשרי.
            </p>

            <button
              type="button"
              className="success-popup-close-btn"
              onClick={closeSuccessPopup}
            >
              סגירה
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;